import { ChangeDetectionStrategy, Component, input, output, effect, DestroyRef, inject, signal, computed, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ReferentielDonneeAnalyse } from 'src/app/bo/referentielDonneeAnalyse';
import { Tir } from 'src/app/bo/tir';
import { ConfigurationTreeview } from 'src/app/bo/treeview/configuration-treeview';
import { ConfigurationTreeviewEnum } from 'src/app/bo/treeview/configuration-treeview.enum';
import { FilterTreeviewItem } from 'src/app/bo/treeview/filter-treeview-item';
import { TreeviewConfig } from 'src/app/bo/treeview/treeview-config';
import { TreeviewHeaderTemplateContext } from 'src/app/bo/treeview/treeview-header-template-context';
import { TreeviewItem, TreeviewItemTypeEnum, TreeviewSelection } from 'src/app/bo/treeview/treeview-item';
import { TreeviewEventParser } from 'src/app/modules/treeview-event-parser';
import { TreeviewHelper } from 'src/app/modules/treeview-helpers';
import { TraductionService } from 'src/app/services/traduction.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { HttpService } from 'src/app/services/http.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { Constants } from 'src/app/utils/constants';
import { Zone } from 'src/app/bo/zone';
import { TypeTir } from 'src/app/bo/TypeTir';
import { Projet } from 'src/app/bo/projet';
import { TirModaleComponent } from '../tir-modale/tir-modale.component';
import posthog from 'posthog-js';
import { DonneeAnalyse } from 'src/app/bo/donneeAnalyse';

// Composant basé sur https://github.com/leovo2708/ngx-treeview
@Component({
  selector: 'app-treeview',
  templateUrl: './treeview.component.html',
  styleUrls: ['./treeview.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TreeviewComponent {
  private readonly destroyRef = inject(DestroyRef);
  
  readonly listeTirs = input<Tir[]>();
  readonly selectionActivee = input(false);
  readonly noeudSelectionne = input<string>();
  readonly tirSelectionne = input<number>();
  readonly listeDonneesAnalyse = input<ReferentielDonneeAnalyse>();
  readonly defaultKPI = input<number>();
  readonly popupOuverte = input<Observable<number>>();
  readonly popupFermee = input<Observable<number>>();
  readonly donneesAnalyseProjet = input<DonneeAnalyse[]>([]);
  readonly selectedTirs = output<any[]>();
  readonly selectedTirsPolygoneType = output<number[]>();
  readonly selectedKPI = output<number>();
  readonly centrerTir = output<any>();
  readonly siteId = input<number>();
  readonly projetId = input<number>();
  readonly tirCree = output<Tir>();

  // Signal-based state
  readonly config = signal<TreeviewConfig>(null);
  readonly allItem = signal<TreeviewItem>(null);
  readonly TreeviewItemTypeEnum = TreeviewItemTypeEnum;
  readonly filterText = signal('');
  readonly items = signal<TreeviewItem[]>([]);
  readonly filterItems = computed(() => {
    const text = this.filterText();
    if (text === '') {
      return this.items();
    }
    const filterItems: TreeviewItem[] = [];
    const filterTextLower = text.toLowerCase();
    this.items().forEach(item => {
      const newItem = this.filterItem(item, filterTextLower);
      if (newItem !== null && newItem !== undefined) {
        filterItems.push(newItem);
      }
    });
    return filterItems;
  });
  readonly hasFilterItems = computed(() => this.filterItems().length > 0);
  readonly selection = signal<TreeviewSelection>({ checkedItems: [], uncheckedItems: [] });
  readonly headerTemplateContext = signal<TreeviewHeaderTemplateContext>(null);
  
  // Filter state
  readonly vueActuelle = signal<'filtrage' | 'parametres' | 'treeview'>('treeview');
  readonly actifProjectsOnly = signal(false);
  readonly nettoyerFiltres = signal(false);
  
  // Configuration state
  readonly configurationTreeview = signal<ConfigurationTreeview>(new ConfigurationTreeview());
  readonly configurationTreeviewNiveauUn = Constants.ConfigurationNiveauUn;
  readonly configurationTreeviewNiveauDeux = signal<{ id: number, nom: string }[]>([]);
  readonly configurationTreeviewNiveauTrois = signal<{ id: number, nom: string }[]>([]);
  readonly configurationFiltrage = signal<any>(null);
  
  // Lists and selections
  readonly listeProjets = signal<string[]>([]);
  readonly projetsSelectionnes = signal<string[]>([]);
  readonly listeZones = signal<string[]>([]);
  readonly zonesSelectionnes = signal<string[]>([]);
  readonly listeTypes = signal<string[]>([]);
  readonly typesSelectionnes = signal<string[]>([]);
  readonly listeBlasted = signal<{ value: boolean, label: string }[]>([]);
  readonly blastedSelectionne = signal<boolean>(null);
  readonly niveauxSelectionnes = signal('');
  readonly niveauxFiltres = signal<string[]>([]);
  readonly erreurFiltreNiveaux = signal(false);
  
  // Date filtering
  readonly dateDebut = signal<Date>(null);
  readonly dateFin = signal<Date>(null);
  readonly periode = signal<Date[]>([]);
  readonly dateJour = new Date();
  
  // Selection state
  readonly donneesAnalyseSelectionnes = signal<number>(null);
  readonly polygoneTypeSelectionnes = signal<number[]>([]);
  readonly tirsChecked = signal<number[]>([]);
  readonly includeCompletedProjects = signal(false);
  
  // UI state
  readonly tirPopup = signal<number>(null);
  readonly currentDate = new Date();
  readonly modaleAjout = signal<BsModalRef>(null);

  constructor(private readonly eventParser: TreeviewEventParser, private readonly defaultConfig: TreeviewConfig,
    public utilService: UtilisateurService, private readonly traductionService: TraductionService, public router: Router,
    private readonly http: HttpService, private readonly fonctionsService: FonctionsService, private readonly modalService: BsModalService, 
    private readonly translate: TranslateService) {
    this.config.set(this.defaultConfig);
    const cfg = this.config();
    cfg.hasAllCheckBox = true;
    cfg.hasFilter = true;
    cfg.hasCollapseExpand = false;
    cfg.maxHeight = 600;

    this.allItem.set(new TreeviewItem({ text: 'All', value: undefined, type: TreeviewItemTypeEnum.Projet }));

    effect(() => {
      this.utilService.currentSite();
      untracked(() => this.chargementConfiguration());
    });

    // Track BOTH listeTirs and tirSelectionne so the selection follows route changes
    // even when the new tirId arrives in a different microtask than the listeTirs update.
     effect(() => {
      this.utilService.currentSite();
      const tirs = this.listeTirs();
      this.tirSelectionne();
      if (tirs !== null && tirs !== undefined) {
        untracked(() => this.initialisation());
      }
    });
  }

  ngOnInit(): void {
    this.chargementConfiguration();
    this.initializeBlastLabels();
    this.createHeaderTemplateContext();
    
    // Écouter les changements de langue pour mettre à jour les labels
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.initializeBlastLabels();
    });
    
    if (this.popupOuverte() != undefined)
      this.popupOuverte().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tirId) => {
        this.tirPopup.set(tirId);
        //ouvrir le treeview jusqu'au tir
      });
    if (this.popupFermee() != undefined)
      this.popupFermee().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tirId) => {
        this.tirPopup.set(null);
      });
  }

  // ngOnChanges replaced by an effect() in the constructor that tracks both
  // listeTirs and tirSelectionne signal inputs.

  private initialisation(reset = true, actifProjectsOnly = false, ) {
    this.actifProjectsOnly.set(actifProjectsOnly);
    if (reset) {   
      this.handleItems();
      this.applyTreeviewFilters(actifProjectsOnly);
    }
    this.generateSelection();
    this.updateFilterItems();
    this.updateCollapsedOfAll();
    this.raiseSelectedChange();
    this.onChangeConfiguration();
  }

  private initializeBlastLabels(): void {
    this.listeBlasted.set([
      { value: true, label: this.translate.instant('GLOBAL.BLASTED') },
      { value: false, label: this.translate.instant('GLOBAL.NOT_BLASTED') }
    ]);
  }

  protected onIncludeCompletedProjectsChange(): void {
    this.projetsSelectionnes.set(
      this.listeProjets().filter(p => 
        this.includeCompletedProjects() || 
        this.listeTirs().find(t => t.nomProjet === p && t.blasted === 0)
      )
    );
  }

  /**
   * Permet de switcher de vue en fonction du menu sélectionné
   * @param vue Vue à afficher
   */
  changerVue(vue: 'filtrage' | 'parametres' | 'treeview'): void {
    if (this.vueActuelle() !== 'treeview' && vue === this.vueActuelle()) {
      this.vueActuelle.set('treeview');
    } else {
      this.vueActuelle.set(vue);
    }
  }

  /**
   * Récupération de la configuration du treeview (en local ou celle du site)
   */
  private chargementConfiguration() {
    const configurationLocale = localStorage.getItem(Constants.StockageConfigurationTreeview + '-' + this.utilService.currentSite().siteId);
    const configFiltrage = localStorage.getItem(Constants.StockageConfigurationFiltrage);
    const configTirs = localStorage.getItem(Constants.StockageTirsChecked);
    if (null !== configurationLocale) {
      this.configurationTreeview.set(JSON.parse(configurationLocale));
    }
    else if (undefined !== this.utilService.currentSite().configurationTreeview) {
      this.configurationTreeview.set(JSON.parse(this.utilService.currentSite().configurationTreeview));
    }
    else this.configurationTreeview.set(new ConfigurationTreeview());
    
    this.configurationFiltrage.set(configFiltrage ? JSON.parse(configFiltrage) : null);
    this.tirsChecked.set(configTirs ? JSON.parse(configTirs) : null);
    this.nettoyerFiltres.set(configFiltrage !== null);
  }

  /**
   * Permet de générer les listes d'éléments de filtrage et d'initialiser la liste des tirs en fonction de la configuration
   */
  private handleItems(): void {
    const tirs = this.listeTirs();
    if (tirs !== null && tirs !== undefined && tirs.length > 0) {
      // On utilise la liste des tirs pour remplir les listes de projets, zones et types de tir
      // Ainsi nous avons toutes les données nécessaires sans avoir à faire d'autres appels au backend
      const projets = [...new Map(tirs.map(item => [item.nomProjet, item])).keys()].sort((a, b) => a.localeCompare(b));
      this.listeProjets.set(projets);
      
      const configFiltrage = this.configurationFiltrage();
      this.projetsSelectionnes.set(configFiltrage ? configFiltrage.listeProjet : projets);
      
      const zones = [...new Map(tirs.map(item => [item['nomZone'], item])).keys()].sort((a, b) => a.localeCompare(b));
      this.listeZones.set(zones);
      this.zonesSelectionnes.set(configFiltrage ? configFiltrage.listeZones : zones);

      const types = [...new Map(tirs.map(item => [item['nomTypeTir'], item])).keys()].sort((a, b) => a.localeCompare(b));
      this.listeTypes.set(types);

      this.typesSelectionnes.set(configFiltrage ? configFiltrage.listeTypes : types);
      this.niveauxSelectionnes.set(configFiltrage ? configFiltrage.listeNiveaux : '');
      this.blastedSelectionne.set(configFiltrage ? configFiltrage.blastedSelectionne : null);

      // if (this.listePolygoneTypes) {
      //   var defaultPolygoneType = this.listePolygoneTypes.find(e => e.default == true);
      //   this.polygoneTypeSelectionnes = configFiltrage ? configFiltrage.polygoneTypeSelectionne : [defaultPolygoneType.tirsPolygoneTypeId];
      // }

      const donneesAnalyse = configFiltrage ? configFiltrage.kpiSelectionne : null;
      this.donneesAnalyseSelectionnes.set(donneesAnalyse);

      if (donneesAnalyse === null || donneesAnalyse === undefined) {
        const defaultKPI = this.defaultKPI();
        this.donneesAnalyseSelectionnes.set(defaultKPI ? defaultKPI : null);
      }

      if (this.selectionActivee()) {
        this.selectedKPI.emit(this.donneesAnalyseSelectionnes());
        this.selectedTirsPolygoneType.emit(this.polygoneTypeSelectionnes());
      }

      if (configFiltrage !== null && configFiltrage.periode.length > 0) {
        const debut = new Date(configFiltrage.periode[0]);
        const fin = new Date(configFiltrage.periode[1]);
        this.dateDebut.set(debut);
        this.dateFin.set(fin);
        this.periode.set([debut, fin]);
      }

      const listeTreeviewItems: TreeviewItem[] = [];
      this.getTreeviewItemsFromBlasts(listeTreeviewItems, tirs, 0, '');
      this.items.set(listeTreeviewItems);
    }
  }

  /**
   * Fonction récursive permettant de passer d'une liste de tirs à une liste de TreeviewItem conformément à la configuration du site
   * @param treeviewItems Tableau de TreeviewItem à remplir
   * @param tabTirs Tableau de tirs
   * @param index Index du niveau de configuration
   */
  private getTreeviewItemsFromBlasts(treeviewItems: TreeviewItem[], tabTirs: Tir[], index: number, nomParent: string) {
    const tirSelected = tabTirs.find(e => e.tirId === Number(this.tirSelectionne()));
    const property = this.getBlastProperty(index);
    // On récupère les différentes valeurs contenues dans la liste pour une propriété
    const keysValueList = this.getKeysList(tabTirs, property);
    keysValueList.forEach((keyValue: string) => {

      // Filtrage des tirs correspondant à la valeur de la clé sur laquelle on boucle
      const filteredBlasts = this.getChildrenBlasts(tabTirs, property, keyValue);

      if (filteredBlasts.length !== 0) {
        const treeviewItem = new TreeviewItem({
          text: keyValue !== null && keyValue !== undefined ? keyValue.toString() : this.traductionService.translate.instant('GLOBAL.NON_RENSEIGNE'),
          value: filteredBlasts[0].projetId
        });
        let treeviewItemChildren: TreeviewItem[] = [];
        // On descend dans l'arborescence des enfants tant que l'on n'est pas au dernier niveau de la configuration
        if (index < 2 && this.getBlastProperty(index + 1) !== null) {
          this.getTreeviewItemsFromBlasts(treeviewItemChildren, filteredBlasts, index + 1, nomParent += treeviewItem.text);
        }
        // Arrivé au dernier niveau, on ajoute les tirs correspondant à la valeur de la clé sur laquelle on boucle
        else {
          filteredBlasts.forEach((tir) => {
            var isChecked = false;
            let isBlasted = tir.blasted == 1;
            const checked = this.tirsChecked();
            if (checked != null && checked.length > 0) isChecked = checked.find(e => e == tir.tirId) ? true : false;
            else isChecked = !(new Date(filteredBlasts[0].dateFinProjet).getTime() < new Date().getTime());
            const treeviewItemTir = new TreeviewItem({
              text: tir.nom,
              value: tir.tirId,
              type: tir.estRealise ? TreeviewItemTypeEnum.TirRealise : TreeviewItemTypeEnum.Tir,
              projetId: tir.projetId,
              checked: isChecked
            }, false,isBlasted);
            treeviewItemChildren.push(treeviewItemTir);
          });
        }

        // Gestion de l'ouverture/fermeture du noeud
        var contientTirSelectionne = false;
        if (tirSelected != undefined) {
          contientTirSelectionne = treeviewItemChildren.map(o => o.value).includes(this.tirSelectionne());
        }
        const aNoeudSelectionne = treeviewItemChildren.map(o => o.identifiant).some(o => o == this.noeudSelectionne());
        const aTirNonRealise = filteredBlasts.some(o => o.estRealise === false);
        treeviewItem.collapsed = contientTirSelectionne || aTirNonRealise || aNoeudSelectionne ? false : true;

        treeviewItem.children = treeviewItemChildren;
        // Ajout d'un type sur le TreeviewItem afin de personnaliser le traitement dans la vue
        // Dans le cas d'une autre propriété, on renseigne le projetId pour pouvoir l'utiliser dans l'URL
        // et la propriété value contient les id des tirs enfants
        if (property === 'nomProjet') {
          treeviewItem.type = TreeviewItemTypeEnum.Projet;
          if (tirSelected != undefined) {
            treeviewItem.collapsed = filteredBlasts.find(e => e.projetId === tirSelected.projetId) ? false : true;
          } else {
            treeviewItem.collapsed = filteredBlasts[0].dateFinProjet 
                ? new Date(filteredBlasts[0].dateFinProjet).getTime() < new Date().getTime()
                : (filteredBlasts[0].dateFinPrevisionnelleProjet 
                    ? new Date(filteredBlasts[0].dateFinPrevisionnelleProjet).getTime() < new Date().getTime() 
                    : false);
          }
        } else {
          treeviewItem.projetId = filteredBlasts[0].projetId;
          treeviewItem.value = treeviewItem.children.map(o => o.value).join(',');
          treeviewItem.identifiant = escape(window.btoa(nomParent + treeviewItem.text));
        }
        const noeudSel = this.noeudSelectionne();
        if (noeudSel !== null && noeudSel !== undefined && noeudSel == treeviewItem.identifiant) {
          this.utilService.setCurrentNode(treeviewItem.text);
          this.utilService.setTirsSelectionnes(treeviewItem.value.split(',').map(o => +o));
        }
        if (treeviewItems === null || treeviewItems === undefined) {
          treeviewItems = [];
        }
        treeviewItems.push(treeviewItem);
      }


    });
  }

  /**
   * Retourne le nom de la propriété de Tir à utiliser à partir d'un niveau de la configuration du treeview
   * @param index Index du niveau de la configuration (0 à 2 attendu)
   */
  private getBlastProperty(index: number): (keyof Tir) {
    const config = this.configurationTreeview();
    let typeEnum: ConfigurationTreeviewEnum;
    switch (index) {
      case 0:
        typeEnum = config.niveauUn;
        break;
      case 1:
        typeEnum = config.niveauDeux;
        break;
      case 2:
        typeEnum = config.niveauTrois;
        break;
    }
    switch (typeEnum) {
      case ConfigurationTreeviewEnum.Projet:
        return 'nomProjet';
      case ConfigurationTreeviewEnum.Zone:
        return 'nomZone';
      case ConfigurationTreeviewEnum.TypeTir:
        return 'nomTypeTir';
      case ConfigurationTreeviewEnum.Niveau:
        return 'niveau';
      case ConfigurationTreeviewEnum.Annee:
        return 'dateTirEffective';
      case ConfigurationTreeviewEnum.month:
        return 'dateTirPrevisionnelle';
      case ConfigurationTreeviewEnum.Aucun:
        return null;
    }
  }

  /**
   * Retourne la liste des valeurs uniques contenues dans un tableau de Tir pour une propriété
   * @param tabTirs Tableau de Tir à inspecter
   * @param propriete Propriété à inspecter
   */
  private getKeysList(tabTirs: Tir[], propriete: (keyof Tir)) {
    switch (propriete) {
      case 'dateTirEffective':
        return [...new Map(tabTirs.map(item => [Tir.GetBestDate(item) !== null ? new Date(Tir.GetBestDate(item))?.getFullYear() : null, item])).keys()].sort((a: number, b: number) => {
          if (a === b) {
            return 0;
          }
          else if (a === null) {
            return 1;
          }
          else if (b === null) {
            return -1;
          }
          return a - b;
        })
          .reverse();

      case 'dateTirPrevisionnelle':
        return [...new Map(tabTirs.map(item => {
          const date = Tir.GetBestDate(item);
          if (date !== null) {
            const d = new Date(date);
            // Clé = "YYYY-MM - Mois" (ex: "2024-01 - Janvier")
            const key = `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)} - ${d.toLocaleString('default', { month: 'long' }).charAt(0).toUpperCase() + d.toLocaleString('default', { month: 'long' }).slice(1).toLowerCase()}`;
            return [key, item];
          }
          return [null, item];
        })).keys()]
          .filter(k => k !== null)
          .sort((a: string, b: string) => {
            // Trie d'abord par année, puis par mois (ordre décroissant)
            const [aYear, aMonth] = a.split(' - ')[0].split('-').map(Number);
            const [bYear, bMonth] = b.split(' - ')[0].split('-').map(Number);
            if (aYear !== bYear) return bYear - aYear;
            return bMonth - aMonth;
      });
      case 'niveau':
        return [...new Map(tabTirs.map(item => [item[propriete], item])).keys()].sort((a: string, b: string) => {
          if (a === b) {
            return 0;
          }
          else if (a === null) {
            return 1;
          }
          else if (b === null) {
            return -1;
          }
          return a.localeCompare(b);
        });
      case 'nomProjet':
        const tab = [...new Map(tabTirs.map(item => [item[propriete], item]))].sort((a: [string, Tir], b: [string, Tir]) => {
          return new Date(b[1].dateFinProjet).getTime() - new Date(a[1].dateFinProjet).getTime();
        });
        return [...tab.map(item => item[0])];
      default:
        return [...new Map(tabTirs.map(item => [item[propriete], item])).keys()].sort((a: string, b: string) => a.localeCompare(b));
    }
  }

  /**
   * Retourne la liste des tirs dont la valeur de la propriété indiquée correspond à la recherche
   * @param tabTirs Tableau de Tir
   * @param propriete Propriété à filtrer
   * @param valeur Valeur à rechercher sur la propriété des tirs
   */
  private getChildrenBlasts(tabTirs: Tir[], propriete: (keyof Tir), valeur: string | number) {
    switch (propriete) {
      case 'dateTirEffective':
        return tabTirs.filter(o => new Date(Tir.GetBestDate(o)).getFullYear() === valeur).sort((a, b) => a.nom.localeCompare(b.nom)).reverse();
      case 'dateTirPrevisionnelle':
      return tabTirs.filter(tir => { 
        const date = Tir.GetBestDate(tir);
        if (date !== null) {
          const d = new Date(date);
          const key = `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)} - ${d.toLocaleString('default', { month: 'long' }).charAt(0).toUpperCase() + d.toLocaleString('default', { month: 'long' }).slice(1).toLowerCase()}`;
          return key === valeur;
        }
    return false;
  }).sort((a, b) => a.nom.localeCompare(b.nom)).reverse();  
      case 'niveau':
        return tabTirs.filter(o => o[propriete] === valeur).sort((a, b) => a.nom.localeCompare(b.nom));
      default:
        return tabTirs.filter(o => o[propriete] === valeur).sort((a, b) => {

          if (Tir.GetBestDate(a) < Tir.GetBestDate(b)) return 1;
          else if (Tir.GetBestDate(a) > Tir.GetBestDate(b)) return -1;

          else if (a.nom < b.nom) {
            return -1;
          }
          else if (a.nom > b.nom) {
            return 1;
          }
          else {
            return 0;
          }


         })
   }
  }





  /**
   * Application du filtrage renseigné par l'utilisateur
   */
  onSubmitFiltrage() {
    // Ces filtres sont liés à des propriétés obligatoirement renseignées à la création d'un tir
    // Si un de ces filtres n'a pas de valeur alors on ne peut rien filtrer
    const filtresValides = this.projetsSelectionnes().length > 0 && this.zonesSelectionnes().length > 0 && this.typesSelectionnes().length > 0;
    this.validationFiltreNiveaux();
    if (filtresValides && !this.erreurFiltreNiveaux()) {
      if (this.selectionActivee()) {
        this.selectedKPI.emit(this.donneesAnalyseSelectionnes());
        this.selectedTirsPolygoneType.emit(this.polygoneTypeSelectionnes());
      }
      this.applyTreeviewFilters();
      this.initialisation(false);
      this.changerVue('treeview');
      this.persistFiltersData();
      this.nettoyerFiltres.set(true);
      this.raiseSelectedChange();

      //this.handleItems();
    } 
  }

  /**
   * Découpage des niveaux renseignés par l'utilisateur en un tableau de niveaux autorisés
   */
  private validationFiltreNiveaux(): void {
    this.niveauxFiltres.set([]);
    this.erreurFiltreNiveaux.set(false);
    const niveauxSel = this.niveauxSelectionnes();
    if (niveauxSel && niveauxSel.trim() !== '') {
      const niveauxSelectionnesTab = niveauxSel.split(';');
      const niveauxArray: string[] = [];
      niveauxSelectionnesTab.forEach(niveaux => {
        niveaux = niveaux.trim();
        if (niveaux.indexOf('-') > -1) {
          const debut = Number(niveaux.split('-')[0]);
          const fin = Number(niveaux.split('-')[1]);
          if (Number.isNaN(debut) || Number.isNaN(fin) || debut > fin) {
            this.erreurFiltreNiveaux.set(true);
            return;
          }
          for (let i = debut; i <= fin; i++) {
            niveauxArray.push(i.toString());
          }
        } else {
          niveauxArray.push(niveaux.toLocaleUpperCase());
        }
      });
      this.niveauxFiltres.set(niveauxArray);
    }
  }

  /**
   * Application des filtres personnalisés de l'utilisateur
   */
  private applyTreeviewFilters(actifProjectsOnly = false): void {
    const niveaux = this.niveauxFiltres();
    if (niveaux === null || niveaux === undefined || niveaux.length === 0) {
      this.validationFiltreNiveaux();
    }
    var listeTirsFiltres: Tir[] = [];

    const tirs = this.listeTirs();
    const projets = this.projetsSelectionnes();
    const zones = this.zonesSelectionnes();
    const types = this.typesSelectionnes();
    const debut = this.dateDebut();
    const fin = this.dateFin();
    const niveauxFilt = this.niveauxFiltres();
    const blasted = this.blastedSelectionne();

    tirs.forEach(tir => {
      
      let dateTir = tir.dateTirEffective ? new Date(tir.dateTirEffective).getTime() : null;
     
      if(dateTir == null){
        dateTir = tir.dateTirPrevisionnelle ? new Date(tir.dateTirPrevisionnelle).getTime() : null;
      }

      const filtreProjet = projets.indexOf(tir.nomProjet) !== -1;
      const filtreZone = zones.indexOf(tir.nomZone) !== -1;
      const filtreType = types.indexOf(tir.nomTypeTir) !== -1;
      const filtreDate = debut && fin ? dateTir !== null && dateTir >= debut.getTime() && dateTir <= fin.getTime() : true;
      const filtreNiveau = niveauxFilt.length === 0 || niveauxFilt.indexOf(tir.niveau?.toLocaleUpperCase()) !== -1;

      const filtreBlasted = blasted === null || 
        (blasted === true && tir.blasted === 1) || 
        (blasted === false && tir.blasted === 0);
        
      if (filtreProjet && filtreZone && filtreType && filtreDate && filtreNiveau && filtreBlasted) {
        listeTirsFiltres.push(tir);
      }
    });

    if (actifProjectsOnly) {
      const currentDate = new Date();
      listeTirsFiltres = listeTirsFiltres.filter(tir => !tir.dateFinProjet || new Date(tir.dateFinProjet) >= currentDate);
    }


    const listeTreeviewItems: TreeviewItem[] = [];
    this.getTreeviewItemsFromBlasts(listeTreeviewItems, listeTirsFiltres, 0, '');
    this.items.set(listeTreeviewItems);
  }

  /**
   * Enregistrement dans le local storage de la configuration du treeview pour le site courant
   */
  onSubmitRegroupement() {
    const cfg = this.configurationTreeview();
    if (cfg.niveauDeux === ConfigurationTreeviewEnum.Aucun) {
      cfg.niveauTrois = ConfigurationTreeviewEnum.Aucun;
      this.configurationTreeview.set({ ...cfg });
    }
    localStorage.setItem(Constants.StockageConfigurationTreeview + '-' + this.utilService.currentSite().siteId, JSON.stringify(this.configurationTreeview()));
    this.initialisation(true,false);
    this.changerVue('treeview');
  }

  /**
   * Adaptation des listes de valeurs disponibles pour chacun des niveaux de configuration du treeview
   */
  onChangeConfiguration(): void {
    const cfg = this.configurationTreeview();
    this.configurationTreeviewNiveauDeux.set(Constants.ConfigurationNiveauxAutres.filter(o => o.id !== cfg.niveauUn));
    this.configurationTreeviewNiveauTrois.set(Constants.ConfigurationNiveauxAutres.filter(o => (o.id !== cfg.niveauUn && o.id !== cfg.niveauDeux) || o.id === ConfigurationTreeviewEnum.Aucun));

    if (cfg.niveauUn === cfg.niveauDeux) {
      cfg.niveauDeux = ConfigurationTreeviewEnum.Aucun;
      cfg.niveauTrois = ConfigurationTreeviewEnum.Aucun;
    } else if (cfg.niveauDeux === cfg.niveauTrois || cfg.niveauUn === cfg.niveauTrois) {
      cfg.niveauTrois = ConfigurationTreeviewEnum.Aucun;
    } else if (cfg.niveauDeux === ConfigurationTreeviewEnum.Aucun) {
      cfg.niveauTrois = ConfigurationTreeviewEnum.Aucun;
    }
    this.configurationTreeview.set({ ...cfg });
  }

  setConfigNiveauUn(value: number): void {
    this.configurationTreeview.update(c => ({ ...c, niveauUn: value }));
  }

  setConfigNiveauDeux(value: number): void {
    this.configurationTreeview.update(c => ({ ...c, niveauDeux: value }));
  }

  setConfigNiveauTrois(value: number): void {
    this.configurationTreeview.update(c => ({ ...c, niveauTrois: value }));
  }

  dateChange(e: any) {
    if (e !== null && e !== undefined && e.length > 1 && e[0] && e[1]) {
      const debut = new Date(e[0]);
      debut.setHours(0, 0, 0, 0);
      const fin = new Date(e[1]);
      fin.setHours(0, 0, 0, 0);
      this.dateDebut.set(debut);
      this.dateFin.set(fin);
      this.periode.set([debut, fin]);
    } else {
      this.dateDebut.set(null);
      this.dateFin.set(null);
    }
  }

  clearDates() {
    this.periode.set([]);
    this.dateDebut.set(null);
    this.dateFin.set(null);
  }

  clearFilters(event: Event): void {
    event.stopPropagation();
    this.projetsSelectionnes.set(this.listeProjets());
    this.zonesSelectionnes.set(this.listeZones());
    this.typesSelectionnes.set(this.listeTypes());
    if (this.selectionActivee()) {
      this.donneesAnalyseSelectionnes.set(null);
      this.selectedKPI.emit(null);
      // if (this.listePolygoneTypes) {
      //   var defaultPolygoneType = this.listePolygoneTypes.find(e => e.default == true);
      //   this.polygoneTypeSelectionnes = [defaultPolygoneType.tirsPolygoneTypeId];
      // }
      this.selectedTirsPolygoneType.emit(this.polygoneTypeSelectionnes());
    }
    this.niveauxSelectionnes.set('');
    this.niveauxFiltres.set([]);
    this.blastedSelectionne.set(null);
    this.clearDates();
    this.configurationFiltrage.set(null);
    localStorage.removeItem(Constants.StockageTirsChecked);
    localStorage.removeItem(Constants.StockageConfigurationFiltrage);
    this.applyTreeviewFilters();
    this.initialisation();
    this.nettoyerFiltres.set(false);
  }

  private persistFiltersData(): void {
    const filtrage = {
      listeProjet: this.projetsSelectionnes(),
      listeZones: this.zonesSelectionnes(),
      listeTypes: this.typesSelectionnes(),
      periode: this.dateDebut() && this.dateFin() ? [this.dateDebut(), this.dateFin()] : [],
      listeNiveaux: this.niveauxSelectionnes(),
      kpiSelectionne: this.donneesAnalyseSelectionnes(),
      polygoneTypeSelectionne: this.polygoneTypeSelectionnes(),
      blastedSelectionne: this.blastedSelectionne()
    };
    this.configurationFiltrage.set(filtrage);
    localStorage.setItem(Constants.StockageConfigurationFiltrage, JSON.stringify(filtrage));
  }

  handleNodeRouting(nom: string, projetId: string, blastsId: string, identifiant: string) {
    this.utilService.setCurrentNode(nom);
    this.utilService.setTirsSelectionnes(blastsId.split(',').map(o => +o));
    this.router.navigate(['/mysite', this.utilService.currentSite().siteId, 'myprojects', projetId], { queryParams: { nodeId: identifiant } });
  }

  getEscapedString(value: string): string {
    return escape(value);
  }

  //#region Méthodes liées au treeview (https://github.com/leovo2708/ngx-treeview)

  onAllCollapseExpand(): void {
    this.allItem().collapsed = !this.allItem().collapsed;
    this.filterItems().forEach(item => item.setCollapsedRecursive(this.allItem().collapsed));
  }

  onFilterTextChange(text: string): void {
    this.filterText.set(text);
    this.updateFilterItems();
  }

  onCheckboxActiveProjectChange(checked: boolean) {
    this.actifProjectsOnly.set(checked);
    this.initialisation(true, checked);
  }

  onAllCheckedChange(): void {
    const checked = this.allItem().checked;
    this.filterItems().forEach(item => {
      item.setCheckedRecursive(checked);
      if (item instanceof FilterTreeviewItem) {
        item.updateRefChecked();
      }
    });

    this.raiseSelectedChange();
  }

  onItemCheckedChange(item: TreeviewItem, checked: boolean): void {
    if (item instanceof FilterTreeviewItem) {
      item.updateRefChecked();
    }
    this.updateCheckedOfAll();
    this.raiseSelectedChange();
  }

  onLabelClick(item: TreeviewItem) {
    if (item.type == TreeviewItemTypeEnum.Tir || item.type == TreeviewItemTypeEnum.TirRealise) {
      this.centrerTir.emit(item.value);
    }
  }

  raiseSelectedChange(): void {
    this.generateSelection();
    const values = this.eventParser.getSelectedChange(this);
    setTimeout(() => {
      this.selectedTirs.emit(values);
      localStorage.setItem(Constants.StockageTirsChecked, JSON.stringify(values));
      this.tirsChecked.set(values);
    });
  }

  private createHeaderTemplateContext(): void {
    this.headerTemplateContext.set({
      config: this.config(),
      item: this.allItem(),
      onCheckedChange: () => this.onAllCheckedChange(),
      onCollapseExpand: () => this.onAllCollapseExpand(),
      onFilterTextChange: (text) => this.onFilterTextChange(text)
    });
  }

  private generateSelection(): void {
    let checkedItems: TreeviewItem[] = [];
    let uncheckedItems: TreeviewItem[] = [];
    const items = this.items();
    if (items !== null && items !== undefined) {
      const selection = TreeviewHelper.concatSelection(items, checkedItems, uncheckedItems);
      checkedItems = selection.checked;
      uncheckedItems = selection.unchecked;
    }

    this.selection.set({
      checkedItems,
      uncheckedItems
    });
  }

  private updateFilterItems(): void {
    this.updateCheckedOfAll();
  }

  private filterItem(item: TreeviewItem, filterText: string): TreeviewItem {
    const isMatch = item.text.toLowerCase().includes(filterText);
    if (isMatch) {
      return item;
    } else {
      if (item.children !== null && item.children !== undefined) {
        const children: TreeviewItem[] = [];
        item.children.forEach(child => {
          const newChild = this.filterItem(child, filterText);
          if (newChild !== null && newChild !== undefined) {
            children.push(newChild);
          }
        });
        if (children.length > 0) {
          const newItem = new FilterTreeviewItem(item);
          newItem.collapsed = false;
          newItem.children = children;
          return newItem;
        }
      }
    }

    return undefined;
  }

  private updateCheckedOfAll(): void {
    let itemChecked: boolean = null;
    for (const filterItem of this.filterItems()) {
      if (itemChecked === null) {
        itemChecked = filterItem.checked;
      } else if (itemChecked !== filterItem.checked) {
        itemChecked = undefined;
        break;
      }
    }

    if (itemChecked === null) {
      itemChecked = false;
    }

    this.allItem().checked = itemChecked;
  }

  private updateCollapsedOfAll(): void {
    let hasItemExpanded = false;
    for (const filterItem of this.filterItems()) {
      if (!filterItem.collapsed) {
        hasItemExpanded = true;
        break;
      }
    }

    this.allItem().collapsed = !hasItemExpanded;
  }

  canCreateTir(): boolean {
    if (!this.utilService.currentProjet) {
      return false;
    }
    
    if (!this.utilService.currentProjet.dateFinEffective) {
      return true;
    }
    
    const dateProjet = new Date(this.utilService.currentProjet.dateFinEffective);
    return dateProjet > this.currentDate;
  }

  openModalcreateTir() {
    posthog.capture('openModalcreateTir', {
      siteId: this.siteId()
    });
    var startLoader = setTimeout(() => {
      this.fonctionsService.loading_screen(true);
    }, 300);
    let prefixe = this.utilService.currentProjet.nom + '_';
    let cpt = 0;

    this.utilService.currentProjet.tirs.forEach(tir => {
      if (tir.nom.startsWith(prefixe)) {
        let numero = Number(tir.nom.substring(prefixe.length)) || 0;
        if (numero > cpt)
          cpt = numero;
      }
    });
    cpt++
    let nomTir = prefixe + cpt;
    let zones = this.http.get<Zone[]>('Sites/' + this.siteId() + '/Zones');
    let typeTirs = this.http.get<TypeTir[]>('Sites/' + this.siteId() + '/BlastTypes');
    let typeProjets = this.http.get<Projet[]>('Sites/' + this.siteId() + '/Projects');

    forkJoin([zones, typeTirs, typeProjets]).subscribe(result => {
      var projetsActifs = [];
      for (var i = 0; i < result[2].length; i++) {
        if (result[2][i].estTermine == false) projetsActifs.push(result[2][i]);
      }
      
      const initialState = {
        tabZones: result[0].filter(o => o.isActif),
        tabTypeTirs: result[1],
        nomTir: nomTir,
        projetId: this.utilService.currentProjet.projetId,
        estModif: false,
        tabProjets: projetsActifs
      };
      
      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
      
      this.modaleAjout.set(this.modalService.show(TirModaleComponent,
        {
          backdrop: true,
          ignoreBackdropClick: true,
          keyboard: false,
          class: 'modal-lg',
          animated: false,
          initialState: initialState as Partial<TirModaleComponent>
        }));

      this.modaleAjout().content.modifTir.subscribe(tir => {
        this.fonctionsService.loading_screen(true);
        this.utilService.currentProjet = null;
        this.tirCree.emit(tir);
      });
    }, () => {
      this.fonctionsService.loading_screen(false);
      clearTimeout(startLoader);
      this.translate.get('GLOBAL.ERREUR_RECUP_DONNEES').subscribe(trad => {
        this.fonctionsService.launch_toast(false, false, trad)
      });
    });
  }
  //#endregion
}
